// Parametrische Variablen: projektweit benannte Maße in mm, an die einzelne
// Teil-Maße gebunden werden. „Materialstärke 19 → 16“ wird einmal geändert und
// wirkt durch alle gebundenen Teile. Engine-frei, reine Funktionen.
//
// SPEICHERFORMAT (Projekt-Spalte `variables`, JSON, Default {}):
//   { "Materialstärke": 19, "Sockelhöhe": 100 }
// Bewusst ein schlichtes Objekt Name→Wert statt [{ id, name, value_mm }]:
// Die Bindung am Teil zeigt per NAME auf die Variable
// (meta.bound = { thickness_mm: "Materialstärke" }), weil der Name das ist,
// was Schreiner im Panel und in der Stückliste sehen. Objektschlüssel sind
// von Haus aus eindeutig — die geforderte Namens-Eindeutigkeit ist damit
// strukturell garantiert statt nur geprüft. Eine zusätzliche id wäre ein
// zweiter Schlüsselraum, der nirgends referenziert würde. Preis dafür:
// Umbenennen muss die Bindungen mitziehen — genau das liefert
// renameVariable() als Teil-Updates gleich mit.
//
// BINDBAR sind NUR die drei Maße length_mm / width_mm / thickness_mm.
// Positionen bleiben bewusst außen vor: eine Position ist erst zusammen mit
// Bezugsteil und Richtung eine Aussage („5 mm über dem Boden“) — dafür gibt es
// die Abstands-Constraints in constraints.js. Eine Variable auf pos_y würde
// Teile stattdessen absolut an eine Zahl nageln und mit Heften/Constraints um
// dieselbe Position streiten. Das wäre ein eigenes Thema („parametrische
// Bezüge“) und ist hier nicht enthalten.

import { clampedMeta } from "./resize"

export const BINDABLE_FIELDS = [ "length_mm", "width_mm", "thickness_mm" ]
export const FIELD_LABELS = { length_mm: "Länge", width_mm: "Breite", thickness_mm: "Stärke" }

const MIN_VALUE = 1
const MAX_UNIQUE_TRIES = 1000

// --- Werte und Namen ---------------------------------------------------------

// Ganzzahliger mm-Wert ≥ 1, sonst null (Integer-mm wie überall im Modell)
export function normalizeValue(value) {
  const number = Number(value)
  if (!Number.isFinite(number)) return null
  const rounded = Math.round(number)
  return rounded >= MIN_VALUE ? rounded : null
}

export function normalizeName(name) {
  return typeof name === "string" ? name.trim() : ""
}

// Geladenes JSON säubern: leere Namen, Doppler und unbrauchbare Werte fliegen
// raus, damit der Editor nie mit kaputten Variablen rechnet
export function normalizeVariables(raw) {
  const result = {}
  for (const [ key, value ] of Object.entries(raw ?? {})) {
    const name = normalizeName(key)
    const mm = normalizeValue(value)
    if (name && mm !== null && !Object.hasOwn(result, name)) result[name] = mm
  }
  return result
}

export function variableNames(variables) {
  return Object.keys(variables ?? {})
}

export function getVariable(variables, name) {
  const key = normalizeName(name)
  return key && Object.hasOwn(variables ?? {}, key) ? variables[key] : null
}

// Anlegen oder überschreiben — liefert immer ein NEUES Objekt; unbrauchbarer
// Name/Wert lässt den Bestand unverändert
export function setVariable(variables, name, value) {
  const key = normalizeName(name)
  const mm = normalizeValue(value)
  if (!key || mm === null) return { ...variables }
  return { ...variables, [key]: mm }
}

// Freier Name: „Variable“, „Variable 2“, …
export function uniqueVariableName(variables, base = "Variable") {
  const name = normalizeName(base) || "Variable"
  const current = variables ?? {}
  if (!Object.hasOwn(current, name)) return name
  for (let i = 2; i < MAX_UNIQUE_TRIES; i++) {
    const candidate = `${name} ${i}`
    if (!Object.hasOwn(current, candidate)) return candidate
  }
  return `${name} ${Date.now()}`
}

// --- Bindungen am Teil -------------------------------------------------------

export function boundsOf(part) {
  return part?.meta?.bound ?? {}
}

export function boundName(part, field) {
  const name = boundsOf(part)[field]
  return typeof name === "string" && name !== "" ? name : null
}

// Neues meta mit gesetzter Bindung; name leer/null löst sie wieder. Ein leer
// gewordenes bound verschwindet aus meta (wie bei corners/grooves).
export function withBinding(part, field, name) {
  if (!BINDABLE_FIELDS.includes(field)) return part.meta
  const bound = { ...boundsOf(part) }
  const key = normalizeName(name)
  if (key) bound[field] = key
  else delete bound[field]
  const meta = { ...part.meta }
  if (Object.keys(bound).length > 0) meta.bound = bound
  else delete meta.bound
  return meta
}

// Wert, den das Maß laut Bindung haben müsste — null wenn frei oder wenn die
// Variable nicht (mehr) existiert
export function boundValue(part, field, variables) {
  const name = boundName(part, field)
  return name ? getVariable(variables, name) : null
}

export function partsBoundTo(parts, name) {
  const key = normalizeName(name)
  if (!key) return []
  return parts.filter((part) => BINDABLE_FIELDS.some((field) => boundName(part, field) === key))
}

// --- Umbenennen und Löschen --------------------------------------------------

// Bindungen umhängen (target = null löst sie) — liefert [{ clientId, attrs }]
// nur für tatsächlich betroffene Teile
function rebindUpdates(parts, name, target) {
  return partsBoundTo(parts, name).map((part) => {
    let meta = part.meta
    for (const field of BINDABLE_FIELDS) {
      if (boundName(part, field) === name) meta = withBinding({ ...part, meta }, field, target)
    }
    return { clientId: part.client_id, attrs: { meta } }
  })
}

// → { variables, updates }. Belegte, leere oder unbekannte Namen ändern nichts.
// Die Reihenfolge bleibt erhalten (der Eintrag wird an Ort und Stelle
// umbenannt), damit die Liste im Panel nicht springt.
export function renameVariable(variables, parts, from, to) {
  const current = variables ?? {}
  const oldName = normalizeName(from)
  const newName = normalizeName(to)
  const unchanged = { variables: { ...current }, updates: [] }
  if (!oldName || !newName || !Object.hasOwn(current, oldName)) return unchanged
  if (newName === oldName || Object.hasOwn(current, newName)) return unchanged

  const renamed = Object.fromEntries(
    Object.entries(current).map(([ key, value ]) => [ key === oldName ? newName : key, value ])
  )
  return { variables: renamed, updates: rebindUpdates(parts, oldName, newName) }
}

// → { variables, updates }. Die Bindungen der betroffenen Teile werden gelöst,
// damit keine Teil-Metadaten auf eine verschwundene Variable zeigen.
export function removeVariable(variables, parts, name) {
  const current = variables ?? {}
  const key = normalizeName(name)
  if (!key || !Object.hasOwn(current, key)) return { variables: { ...current }, updates: [] }

  const rest = { ...current }
  delete rest[key]
  return { variables: rest, updates: rebindUpdates(parts, key, null) }
}

// --- Verwaiste Bindungen -----------------------------------------------------

// Eine Bindung kann ins Leere zeigen, obwohl removeVariable() sie sauber löst:
// Undo/Redo, ein aus einem anderen Projekt kopiertes Teil oder ein älterer
// Dokumentstand bringen sie mit. Das Panel zeigt so ein Maß als „<name>
// (fehlt)“ — hier steht, welche Teile betroffen sind und wie man sie los wird.
//
// → [{ part, fields, names }] je betroffenem Teil, Reihenfolge wie in `parts`.
export function orphanBindings(parts, variables) {
  const known = new Set(variableNames(variables))
  const result = []
  for (const part of parts ?? []) {
    const fields = BINDABLE_FIELDS.filter((field) => {
      const name = boundName(part, field)
      return name !== null && !known.has(name)
    })
    if (fields.length === 0) continue
    const names = [ ...new Set(fields.map((field) => boundName(part, field))) ]
    result.push({ part, fields, names })
  }
  return result
}

// Namen der verschwundenen Variablen, auf die noch gezeigt wird (ohne Doppler)
export function orphanNames(parts, variables) {
  return [ ...new Set(orphanBindings(parts, variables).flatMap(({ names }) => names)) ]
}

// Updates, die nur die toten Bindungen lösen — [{ clientId, attrs: { meta } }].
// Die MASSE bleiben, wie sie sind: der Wert der gelöschten Variablen steht ja
// schon im Teil und ist nach dem Lösen genau das, was gebaut werden soll.
// Der Aufrufer packt alles in EINEN UpdatePartsCommand (ein Undo-Schritt).
export function clearOrphanBindings(parts, variables) {
  return orphanBindings(parts, variables).map(({ part, fields }) => {
    let meta = part.meta
    for (const field of fields) meta = withBinding({ ...part, meta }, field, null)
    return { clientId: part.client_id, attrs: { meta } }
  })
}

// --- Wirkung -----------------------------------------------------------------

// Kern des Features: neuer Wert → [{ clientId, attrs }] für alle Teile, die
// mindestens ein Maß an `name` gebunden haben. Bearbeitungen (Ausschnitte,
// Ecken, Nuten/Fälze) werden wie beim Resize im selben Schritt mitgeklemmt.
// Unbekannte Variable, unbrauchbarer Wert oder bereits passendes Maß → [].
// Der Aufrufer packt das Ergebnis in EINEN UpdatePartsCommand.
export function applyVariable(parts, name, value) {
  const mm = normalizeValue(value)
  if (mm === null) return []

  const updates = []
  for (const part of partsBoundTo(parts, name)) {
    const attrs = {}
    for (const field of BINDABLE_FIELDS) {
      if (boundName(part, field) === normalizeName(name) && part[field] !== mm) attrs[field] = mm
    }
    if (Object.keys(attrs).length === 0) continue
    const meta = clampedMeta(part, { ...part, ...attrs })
    if (meta) attrs.meta = meta
    updates.push({ clientId: part.client_id, attrs })
  }
  return updates
}

// --- Generator-Vorgabe -------------------------------------------------------

// Vergleichsform für Namen: Groß-/Kleinschreibung, Umlaute und Trennzeichen
// egal — „Materialstärke“, „materialstaerke“ und „Material-Stärke“ sind
// dasselbe gemeinte Maß
function comparableName(name) {
  return name.toLowerCase()
    .replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss")
    .replace(/[^a-z0-9]/g, "")
}

export const MATERIAL_THICKNESS_NAME = "Materialstärke"

// Vorgabe für Korpus-/Schubladen-Generator: Wert der Materialstärke-Variablen
// oder null, wenn es keine gibt
export function materialThicknessOf(variables) {
  const target = comparableName(MATERIAL_THICKNESS_NAME)
  const key = variableNames(variables).find((name) => comparableName(name) === target)
  return key ? variables[key] : null
}
